import React, { createContext, useReducer, useState, useEffect, ReactNode, useRef, JSX } from 'react'
import { websocketMessage } from './websocketAction'
import { initialState, WebsocketActionTypes, websocketReducer, WebsocketState } from './websocketReducer'
import { newUuid } from '../../functions'

interface WebSocketOptions {
  onOpen?: () => void
  onError?: (event: WebSocketEventMap['error']) => void
  onClose?: (event: WebSocketEventMap['close']) => void
  onMessage?: (event: WebSocketEventMap['message']) => void
}

type WebSocketState =
  | { status: 'INITIALIZING' }
  | { status: 'CONNECTED' }
  | { status: 'DISCONNECTED' }
  | { status: 'ERROR', error: Event }
  | { status: 'CLOSING' }

type UseWebSocketHook = (
  url: string,
  options?: WebSocketOptions
) => WebSocketState

const useWebSocket: UseWebSocketHook = (url, options = {}) => {
  const { onOpen, onError, onClose, onMessage } = options
  const [state, setState] = useState<WebSocketState>({ status: 'INITIALIZING' })
  const webSocketRef = useRef<WebSocket | null>(null)
  const [reconnectTimeoutId, setReconnectTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null)

  const connectWebSocket = (): void => {
    const webSocket = new WebSocket(url)

    webSocket.onopen = () => {
      setState({ status: 'CONNECTED' })
      onOpen?.()
    }
    webSocket.onerror = (event) => {
      setState({ status: 'ERROR', error: event })
      onError?.(event)
    }
    webSocket.onclose = (event) => {
      setState({ status: 'DISCONNECTED' })
      onClose?.(event)
    }
    webSocket.onmessage = (event) => {
      onMessage?.(event)
    }

    webSocketRef.current = webSocket

    const onChangeClose = (): void => {
      setState({ status: 'CLOSING' })
      if (reconnectTimeoutId === null) {
        setReconnectTimeoutId(setTimeout(connectWebSocket, 1000))
      }
    }
    webSocketRef.current.addEventListener('close', onChangeClose)
  }

  useEffect(() => {
    connectWebSocket()

    return () => {
      if (webSocketRef.current) {
        webSocketRef.current?.close()
      }

      if (reconnectTimeoutId !== null) {
        clearTimeout(reconnectTimeoutId)
      }
    }
  }, [])

  return state
}

interface WebsocketContextType {
  websocketState?: WebsocketState
  websocketDispatch?: React.Dispatch<WebsocketActionTypes>
}

export const WebsocketContext = createContext<WebsocketContextType>({ websocketState: initialState })

export const WebsocketProvider = (props: { wsUrl: string, children: ReactNode }): JSX.Element => {
  const [websocketState, websocketDispatch] = useReducer(websocketReducer, initialState)

  const state = useWebSocket(props.wsUrl + '/ws', {
    onMessage: event => {
      const uuid = newUuid()
      // console.log('ws: ' + String(event.data) + '|' + uuid)
      websocketMessage(websocketDispatch, String(event.data), uuid)
    }
  })

  useEffect(() => {
    // console.log(state)
  }, [state])

  return (
    <WebsocketContext.Provider value={{ websocketState, websocketDispatch }}>
      {props.children}
    </WebsocketContext.Provider>
  )
}
